'use client';
import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { BiDollar, BiRightArrowAlt } from 'react-icons/bi';
import LouiseButton from '@/components/buttons/LouiseButton';
import { HiOutlineTrophy } from 'react-icons/hi2';

type Props = { locale: string };

function AllreadyPaid({ locale }: Props) {
  const { t } = useTranslation(locale);

  const stats = [
    {
      value: '$5,847/month',
      label: t('AffiliationPrograms/readyToEarn/stats/1'),
      icon: HiOutlineTrophy,
    },
    {
      value: '$4,230/month',
      label: t('AffiliationPrograms/readyToEarn/stats/2'),
      icon: HiOutlineTrophy,
    },
    {
      value: '$3,690/month',
      label: t('AffiliationPrograms/readyToEarn/stats/3'),
      icon: HiOutlineTrophy,
    },
  ];
  const benefits = t('AffiliationPrograms/readyToEarn/benefits', { returnObjects: true }) as Array<string>;
  const payment = t('AffiliationPrograms/readyToEarn/payment', { returnObjects: true }) as Array<string>;
  const secondBenefits = t('AffiliationPrograms/readyToEarn/secondBenefits', { returnObjects: true }) as Array<string>;

  return (
    <section className="tw-py-20 lg:tw-py-32 tw-bg-transparent tw-font-['Lexend']">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Main Content */}
        <div className="tw-text-center tw-mb-16">
          {/* Badge */}
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-green-100 tw-text-green-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-8">
            <BiDollar className="tw-w-4 tw-h-4" />
            {t('AffiliationPrograms/readyToEarn/badge')}
          </div>

          {/* Title */}
          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
            {t('AffiliationPrograms/readyToEarn/alreadyPaid')}
          </h2>

          {/* Main Amount */}
          <div className="tw-relative tw-inline-block tw-mb-8">
            <div className="tw-text-5xl sm:tw-text-6xl lg:tw-text-7xl tw-font-bold tw-bg-gradient-to-r tw-from-green-600 tw-to-emerald-600 tw-bg-clip-text tw-text-transparent tw-mb-4">
              $512,000
            </div>

            {/* Floating animation elements */}
            <div className="tw-absolute tw--top-4 tw--right-4 tw-w-8 tw-h-8 tw-bg-green-200 tw-rounded-full tw-animate-bounce tw-opacity-60"></div>
            <div className="tw-absolute tw--bottom-2 tw--left-6 tw-w-6 tw-h-6 tw-bg-emerald-300 tw-rounded-full tw-animate-pulse tw-opacity-40"></div>
          </div>

          <p className="tw-text-lg tw-text-gray-600 tw-mb-12 tw-max-w-2xl tw-mx-auto">
            {t('AffiliationPrograms/readyToEarn/thisYear')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="tw-grid sm:tw-grid-cols-3 tw-gap-8 tw-mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="tw-bg-white tw-rounded-2xl tw-p-8 tw-shadow-lg tw-border tw-border-gray-100 tw-text-center tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-105"
            >
              <div
                className={`tw-w-16 tw-h-16 tw-mx-auto tw-mb-4 tw-rounded-full tw-bg-gradient-to-r ${
                  index === 0
                    ? 'tw-from-amber-200 tw-to-amber-400'
                    : index === 1
                    ? 'tw-from-orange-200 tw-to-amber-700'
                    : 'tw-from-gray-200 tw-to-gray-500'
                } tw-flex tw-items-center tw-justify-center`}
              >
                <stat.icon color="white" className={`tw-w-8 tw-h-8`} />
              </div>
              <div className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">{stat.value}</div>
              <div className="tw-text-sm tw-text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="tw-text-center">
          <div className="tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-rounded-3xl tw-p-8 sm:tw-p-12 tw-text-gray-600 tw-relative tw-overflow-hidden">
            {/* Background decorations */}
            <div className="tw-absolute tw-top-0 tw-right-0 tw-w-64 tw-h-64 tw-bg-white/5 tw-rounded-full tw-blur-3xl tw--translate-y-32 tw-translate-x-32"></div>
            <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-48 tw-h-48 tw-bg-white/5 tw-rounded-full tw-blur-2xl tw-translate-y-24 tw--translate-x-24"></div>

            <div className="tw-relative tw-z-10">
              <h3 className="tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-bold tw-mb-4 tw-text-white">
                {t('AffiliationPrograms/readyToEarn/title')}
              </h3>
              <p className="tw-text-blue-100 tw-mb-8 tw-max-w-2xl tw-mx-auto tw-text-lg tw-leading-relaxed">
                {t('AffiliationPrograms/readyToEarn/description')}
              </p>

              {/* Benefits list */}
              <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-6 tw-mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="tw-flex tw-items-center tw-gap-2 tw-text-blue-100">
                    <BiRightArrowAlt className="tw-w-4 tw-h-4" />
                    <span className="tw-text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-6 tw-mb-8">
                {payment.map((benefit, index) => (
                  <div key={index} className="tw-flex tw-items-center tw-gap-2 tw-text-blue-100">
                    <BiRightArrowAlt className="tw-w-4 tw-h-4" />
                    <span className="tw-text-sm">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}

              <LouiseButton
                color="white"
                cta={t('AffiliationPrograms/joinPartner')}
                ctaTarget="other"
                href="partner/business/register"
                icon={<BiDollar className="tw-w-5 tw-h-5" />}
                locale={locale}
              />

              {/* Trust indicator */}
              <p className="tw-mt-6">
                {secondBenefits.map((benefit, index) => {
                  return (
                    <span className="tw-text-blue-200 tw-text-sm" key={index}>
                      • {benefit}
                    </span>
                  );
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom testimonial */}
        <div className="tw-text-center tw-mt-16 tw-bg-gray-50 tw-rounded-2xl tw-p-8">
          <blockquote className="tw-text-md tw-text-gray-700 tw-italic tw-mb-4">
            &#34;{t('AffiliationPrograms/testimonial')}&#34;
          </blockquote>
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-4">
            <div className="tw-w-12 tw-h-12 tw-bg-gradient-to-r tw-from-blue-500 tw-to-purple-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold">
              M.L
            </div>
            <div className="tw-text-left">
              <div className="tw-text-sm tw-text-gray-600">{t('AffiliationPrograms/testimonial-role')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AllreadyPaid;
